import React, { useState } from "react";
import { createPortalSession } from "../../user-management/services/billingService";
import { useAuth } from "../../../context/AuthContext";

/**
 * Shown once Stripe has exhausted its retries and given up (SGL-283).
 *
 * It replaces the page content rather than the whole shell: the sidebar stays,
 * so the customer can still sign out, switch to another Brand they have a Seat
 * on, and see that their work is where they left it. A full-screen takeover
 * would trap a Member Seat whose Owner stopped paying with no way out.
 *
 * A page rather than a modal, because a modal invites dismissing and then
 * wondering why nothing publishes. It is not a punishment screen: the single
 * most important thing it says is that nothing has been deleted, because the
 * fear at this moment is "have I lost my work". Every Brand, post, connection
 * and piece of history is intact, and one payment brings it all back with
 * nothing to set up again.
 */
export const PaymentPaywall: React.FC = () => {
  const { idToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openPortal = async () => {
    if (!idToken) return;
    setLoading(true);
    setError(null);
    try {
      const { url } = await createPortalSession(idToken);
      window.location.href = url;
    } catch (err: any) {
      // 404 means Stripe has no subscription to manage for this User — usually
      // a Member Seat whose Owner stopped paying, or a subscription cancelled
      // outright. Sending them to a portal that cannot help is worse than
      // telling them who can, so this case gets its own sentence.
      setError(
        String(err?.message || "").includes("No active subscription")
          ? "This Brand's subscription is managed by whoever pays for it. Ask them to update the card, or email support@sagelyn.ai and we will sort it out."
          : "Could not open the billing page. Try again, or email support@sagelyn.ai."
      );
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center bg-theme-background p-6">
      <div className="w-full max-w-lg rounded-xl border border-theme-border bg-theme-surface p-8 text-center shadow-sm">
        <span className="material-icons-outlined mb-3 text-4xl text-amber-600">
          pause_circle
        </span>
        <h1 className="mb-2 text-xl font-semibold text-theme-fg">
          Your AI employees are paused
        </h1>
        <p className="mb-5 text-sm leading-relaxed text-theme-fg-muted">
          We tried several times over the past few weeks and couldn&apos;t take payment,
          so publishing has stopped for now.
        </p>

        <div className="mb-6 rounded-lg border border-theme-border bg-theme-background px-4 py-3 text-left text-sm text-theme-fg-muted">
          <p className="mb-1 font-semibold text-theme-fg">Nothing has been deleted.</p>
          <p className="leading-relaxed">
            Your Brands, posts, connected accounts and history are exactly where you
            left them. Update your card and everything picks straight back up, with
            nothing to set up again.
          </p>
        </div>

        <button
          type="button"
          onClick={openPortal}
          disabled={loading || !idToken}
          className="w-full rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-primary-hover disabled:opacity-60"
        >
          {loading ? "Opening…" : "Update payment method"}
        </button>

        {error && <p className="mt-3 text-xs text-red-500">{error}</p>}

        <p className="mt-4 text-xs text-theme-fg-muted">
          Questions? Email{" "}
          <a className="underline" href="mailto:support@sagelyn.ai">
            support@sagelyn.ai
          </a>
        </p>
      </div>
    </div>
  );
};

export default PaymentPaywall;
